import { isEmptyOrSpace } from '../../other/ui/string-helper-functions.js';
import { LangV6Config } from '../../../localization/scripts/v6config/v6config-lang.js';
import type { StringDictionary } from '../types/misc.js';

/**
 * This is a light independant expectation of a "FRAME" in dealer that it can use to process and report on information
 * This provides a decoupled layer between webmodule and v6config or any other future config
 */
export enum FrameValidationType {
  nothing = 0,
  note = 1,
  information = 2,
  warning = 3,
  critical = 4,
  outOfDate = 5
}

export interface FrameValidation {
  type: FrameValidationType;
  title: string;
  visible?: boolean;
  text?: string;
  location?: FramePoint;
  locationGlobal?: FramePoint;
}

export interface FramePoint {
  x: number;
  y: number;
}

export interface FrameViewBox {
  minx: number;
  miny: number;
  width: number;
  height: number;
}

export enum FrameAttributeEditorType {
  Text = 1,
  Dropdown = 2,
  Picker = 3,
  Length = 4,
  Currency = 5,
  Number = 6,
  Boolean = 7,
  Dimension = 8,
  Glazing = 9,
  Macro = 10,
  Quantity = 11,

  RootFrameDimension = 12,
  MacroEqualizeLights = 13,
  BoolTrigger = 14,
  CommandMacro = 15,
  WholeNumber = 16,

  PassthroughValue = 1000,
  CombinationGlazing = 999,
  QuoteOptionProperty = 8000
}

export interface FrameAttributeTemplateValue {
  groupName: string;
  groupDescription: string;
  itemIndex: number;
  code: string;
  description: string;
  dataType: FrameAttributeEditorType;
  value: string;
  displayValue: string;
}
export interface FrameAttributeTemplate {
  frameType: string;
  name: string;

  attributes: FrameAttributeTemplateValue[];
}

export interface FrameAttribute {
  code: string;
  dataType: FrameAttributeEditorType;
  value: string;
  displayValue: string;
  description: string;
  hideFromReport?: boolean;
  hideFromUI?: boolean;
}
export enum FrameAttributeGroupType {
  QuoteItem = 0,
  Frame = 1,
  Quote = 2,
  Validations = 3,
  Igu = 4,
  QuoteAttributeItem = 5
}
export interface FrameAttributeGroup {
  groupType: FrameAttributeGroupType;
  name: string;
  description: string;
  hideFromReport?: boolean;
  hideFromUI?: boolean;
  attributes: FrameAttribute[];
}

export interface FrameBuyInItem {
  resourceType: number;
  code: string;
  partCode: string;
  decription: string;
  quantity: number;
  extraDetails: StringDictionary | null;
}

export interface Frame {
  productLine?: string | null;
  description: string;
  boundary: FrameViewBox;
  lightName?: string;
  attributeGroups: FrameAttributeGroup[];
  nestedFrames: Frame[];
  validations?: FrameValidation[];
  buyIn?: FrameBuyInItem[];
}

// --- V2 Frame Contract - Multi-face Support ---

/**
 * V2 Light: Represents an opening/boundary on a face.
 * Contains nested frames and light-level attributes (IGU/glass).
 */
export interface FrameLight {
  boundaryName: string;
  bounds: FrameViewBox;
  /** Light-level attributes (e.g., IGU/glass configuration for this opening) */
  attributeGroups: FrameAttributeGroup[];
  /** Frames nested inside this light. Empty = empty light (no frame in opening) */
  nestedFrames: FrameV2[];
}

/**
 * V2 Face: Represents a side of a frame (Exterior, Interior, etc.).
 * Each face contains its own set of lights/openings.
 */
export interface FrameFace {
  faceId: number;
  name: string;
  lights: FrameLight[];
}

/**
 * V2 Frame: Supports multi-face design with explicit face/light hierarchy.
 * Structure: FrameV2 → faces[] → lights[] → nestedFrames[] (recursive)
 */
export interface FrameV2 {
  productLine?: string | null;
  description: string;
  boundary: FrameViewBox;
  /** Root-level frame attributes */
  attributeGroups: FrameAttributeGroup[];
  /** Faces of this frame. Single-face: faces.length === 1. Multi-face: faces.length > 1 */
  faces: FrameFace[];
  validations?: FrameValidation[];
  /** BuyIn items - only on top-level frames */
  buyIn?: FrameBuyInItem[];
}

// --- Frame compare types and utilities ---

export type DiffStatus = 'added' | 'removed' | 'changed' | 'unchanged';

export interface FrameAttributeDiff {
  code: string;
  description?: string | null; // like label
  value: { before: string | null; after: string | null };
  displayValue: { before: string | null; after: string | null };
  status: DiffStatus;
}

export interface FrameCompareReport {
  faceName?: string | null;
  lightName?: { before: string | null; after: string | null } | null;
  description?: { before: string | null; after: string | null } | null;
  attributes: FrameAttributeDiff[];
  nested: FrameCompareReport[]; // children (faces for multi-face, frames for single-face)
  frameStatus?: 'added' | 'removed' | 'changed' | 'unchanged';
}

function flattenFrameAttributes(groups: FrameAttributeGroup[] | null | undefined): Map<string, FrameAttribute> {
  const map = new Map<string, FrameAttribute>();
  if (!groups) return map;
  for (const g of groups) {
    if (!g?.attributes) continue;
    for (const a of g.attributes) {
      if (!a?.code) continue;
      map.set(a.code, a);
    }
  }
  return map;
}

function attributeStatus(before: FrameAttribute | undefined, after: FrameAttribute | undefined): DiffStatus {
  if (!before && after) return 'added';
  if (before && !after) return 'removed';
  if (!before && !after) return 'unchanged';
  // both exist: compare effective values (value then displayValue)
  const beforeVal = before?.value ?? '';
  const afterVal = after?.value ?? '';
  const beforeDisp = before?.displayValue ?? '';
  const afterDisp = after?.displayValue ?? '';

  // If both raw values are empty but displayValue matches, treat as unchanged (sanitizer case)
  if (beforeVal === '' && afterVal === '' && beforeDisp === afterDisp) return 'unchanged';

  // If values don't match but displayValues do match, and after value is empty, this is sanitized data
  if (beforeVal !== afterVal && beforeDisp === afterDisp && afterVal === '') return 'unchanged';

  const eff = (x: FrameAttribute | undefined) => {
    if (!x) return '';
    const v = x.value ?? '';
    const dv = x.displayValue ?? '';
    if (v !== '') return v;
    return dv;
  };
  return eff(before) !== eff(after) ? 'changed' : 'unchanged';
}

export interface FrameCompareOptions {
  /** When true, attributes or groups flagged hideFromReport/hideFromUI are excluded from diff */
  excludeHidden?: boolean;
}

export function compareFrames(
  before: FrameV2 | null | undefined,
  after: FrameV2 | null | undefined,
  options?: FrameCompareOptions
): FrameCompareReport | null {
  if (!before && !after) return null;

  // Determine frame status
  let frameStatus: 'added' | 'removed' | 'changed' | 'unchanged';
  if (!before && after) {
    frameStatus = 'added';
  } else if (before && !after) {
    frameStatus = 'removed';
  } else {
    frameStatus = 'changed'; // Will be refined later
  }

  // Compare root attributes
  const attrs = compareAttributes(before?.attributeGroups, after?.attributeGroups, options);

  // Recurse into faces → lights → nestedFrames
  const beforeFaces = before?.faces ?? [];
  const afterFaces = after?.faces ?? [];
  const nestedReports: FrameCompareReport[] = [];

  // Match faces by faceId
  const allFaceIds = new Set<number>();
  for (const f of beforeFaces) allFaceIds.add(f.faceId);
  for (const f of afterFaces) allFaceIds.add(f.faceId);
  const isMultiFace = Math.max(beforeFaces.length, afterFaces.length) > 1;

  for (const faceId of Array.from(allFaceIds).sort((a, b) => a - b)) {
    const bFace = beforeFaces.find(f => f.faceId === faceId) ?? null;
    const aFace = afterFaces.find(f => f.faceId === faceId) ?? null;
    const faceName = isMultiFace ? (aFace?.name ?? bFace?.name ?? null) : null;
    const faceReport = compareFaces(bFace, aFace, faceName, options);
    if (faceReport) nestedReports.push(faceReport);
  }

  // Determine final frame status
  if (frameStatus === 'changed') {
    const allNestedUnchanged = nestedReports.every(n => n.frameStatus === 'unchanged');
    if (attrs.length === 0 && allNestedUnchanged) {
      frameStatus = 'unchanged';
    }
  }

  return {
    description: { before: before?.description ?? null, after: after?.description ?? null },
    attributes: attrs,
    nested: nestedReports,
    frameStatus
  };
}

function compareFaces(
  before: FrameFace | null,
  after: FrameFace | null,
  faceName: string | null,
  options?: FrameCompareOptions
): FrameCompareReport | null {
  if (!before && !after) return null;

  let frameStatus: 'added' | 'removed' | 'changed' | 'unchanged';
  if (!before && after) frameStatus = 'added';
  else if (before && !after) frameStatus = 'removed';
  else frameStatus = 'changed';

  const beforeLights = before?.lights ?? [];
  const afterLights = after?.lights ?? [];

  // Match lights by boundaryName
  const allLightNames = new Set<string>();
  for (const l of beforeLights) allLightNames.add(l.boundaryName);
  for (const l of afterLights) allLightNames.add(l.boundaryName);

  const lightReports: FrameCompareReport[] = [];
  for (const ln of Array.from(allLightNames).sort()) {
    const bLight = beforeLights.find(l => l.boundaryName === ln) ?? null;
    const aLight = afterLights.find(l => l.boundaryName === ln) ?? null;
    const lightReport = compareLights(bLight, aLight, options);
    if (lightReport) lightReports.push(lightReport);
  }

  // If no lights matched by name, fallback to position
  if (allLightNames.size === 0) {
    const max = Math.max(beforeLights.length, afterLights.length);
    for (let i = 0; i < max; i++) {
      const b = beforeLights[i] ?? null;
      const a = afterLights[i] ?? null;
      const lightReport = compareLights(b, a, options);
      if (lightReport) lightReports.push(lightReport);
    }
  }

  if (frameStatus === 'changed') {
    const allUnchanged = lightReports.every(r => r.frameStatus === 'unchanged');
    if (allUnchanged) frameStatus = 'unchanged';
  }

  return {
    faceName: faceName,
    attributes: [],
    nested: lightReports,
    frameStatus
  };
}

function compareLights(
  before: FrameLight | null,
  after: FrameLight | null,
  options?: FrameCompareOptions
): FrameCompareReport | null {
  if (!before && !after) return null;

  let frameStatus: 'added' | 'removed' | 'changed' | 'unchanged';
  if (!before && after) frameStatus = 'added';
  else if (before && !after) frameStatus = 'removed';
  else frameStatus = 'changed';

  // Compare light-level attributes (IGU)
  const attrs = compareAttributes(before?.attributeGroups, after?.attributeGroups, options);

  // Compare nested frames — match by description or position
  const beforeNested = before?.nestedFrames ?? [];
  const afterNested = after?.nestedFrames ?? [];
  const nestedReports: FrameCompareReport[] = [];

  const max = Math.max(beforeNested.length, afterNested.length);
  for (let i = 0; i < max; i++) {
    const b = beforeNested[i] ?? null;
    const a = afterNested[i] ?? null;
    const cmp = compareFrames(b, a, options);
    if (cmp) nestedReports.push(cmp);
  }

  if (frameStatus === 'changed') {
    const allNestedUnchanged = nestedReports.every(n => n.frameStatus === 'unchanged');
    if (attrs.length === 0 && allNestedUnchanged) frameStatus = 'unchanged';
  }

  return {
    lightName: { before: before?.boundaryName ?? null, after: after?.boundaryName ?? null },
    description: null,
    attributes: attrs,
    nested: nestedReports,
    frameStatus
  };
}

function compareAttributes(
  beforeGroups: FrameAttributeGroup[] | null | undefined,
  afterGroups: FrameAttributeGroup[] | null | undefined,
  options?: FrameCompareOptions
): FrameAttributeDiff[] {
  const beforeMap = flattenFrameAttributes(beforeGroups);
  const afterMap = flattenFrameAttributes(afterGroups);
  const allCodes = new Set<string>();
  for (const k of beforeMap.keys()) allCodes.add(k);
  for (const k of afterMap.keys()) allCodes.add(k);

  const attrs: FrameAttributeDiff[] = [];
  const excludeHidden = options?.excludeHidden === true;
  for (const code of Array.from(allCodes).sort()) {
    const b = beforeMap.get(code);
    const a = afterMap.get(code);
    if (excludeHidden && (b?.hideFromReport || b?.hideFromUI || a?.hideFromReport || a?.hideFromUI)) continue;
    const status = attributeStatus(b, a);
    if (status === 'unchanged') continue;
    attrs.push({
      code,
      description: a?.description ?? b?.description ?? null,
      value: { before: b ? (b.value ?? null) : null, after: a ? (a.value ?? null) : null },
      displayValue: { before: b ? (b.displayValue ?? null) : null, after: a ? (a.displayValue ?? null) : null },
      status
    });
  }
  return attrs;
}

function escapeMd(v: any): string {
  if (v === null || v === undefined) return '';
  const s = String(v);
  return s.replace(/\|/g, '\\|').replace(/`/g, '\\`');
}

function normalizeText(input: any): string {
  if (input === null || input === undefined) return '';
  let s = String(input);
  s = s.replace(/\uFFFD/g, '"');
  s = s.replace(/[\u201C\u201D\u201E\u201F]/g, '"');
  s = s.replace(/[\u2018\u2019\u201A\u201B]/g, "'");
  s = s.replace(/\u2026/g, '...');
  s = s.replace(/\u2013/g, '-');
  s = s.replace(/\u2014/g, '--');
  return s;
}

export function isFrameCompareReportEmpty(report: FrameCompareReport | null | undefined): boolean {
  if (!report) return true;
  if (report.attributes && report.attributes.length > 0) return false;
  if (report.nested) {
    for (const n of report.nested) {
      if (!isFrameCompareReportEmpty(n)) return false;
    }
  }
  return true;
}

export function getFrameCompareAsMarkdown(header: string, report?: FrameCompareReport | null): string {
  if (!report || isFrameCompareReportEmpty(report)) {
    return ``;
  }
  const lines: string[] = [];
  if (!isEmptyOrSpace(header)) lines.push(`# ${header}`);
  lines.push('');

  const title = isEmptyOrSpace(header)
    ? undefined
    : (report.description?.after ?? report.description?.before ?? undefined);
  if (title) lines.push(`## ${escapeMd(title)}`);
  lines.push('');

  // Render root-level attributes
  renderAttributes(lines, report.attributes);

  // Render nested: faces → lights → frames
  function renderNested(nested: FrameCompareReport[], crumbs: string[] = []) {
    for (const nf of nested || []) {
      // Face-level node: has faceName, render as face header for multi-face
      if (nf.faceName) {
        const faceCrumbs = crumbs.concat([nf.faceName]);
        if (nf.frameStatus === 'removed') {
          lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(nf.faceName) }));
          lines.push(LangV6Config.frameCompareFrameRemoved());
          lines.push('');
          continue;
        } else if (nf.frameStatus === 'added') {
          lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(nf.faceName) }));
          lines.push(LangV6Config.frameCompareFrameAdded());
          lines.push('');
          continue;
        }
        // Render face header only if it has changes
        if (nf.frameStatus !== 'unchanged') {
          lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(nf.faceName) }));
          lines.push('');
        }
        renderNested(nf.nested, faceCrumbs);
        continue;
      }

      // Light or frame-level node
      const current =
        nf.lightName?.after ?? nf.lightName?.before ?? nf.description?.after ?? nf.description?.before ?? null;

      // Single-face wrapper with no identifying name — skip it in the breadcrumb
      if (current === null && !nf.faceName) {
        renderAttributes(lines, nf.attributes);
        if (nf.nested?.length) {
          renderNested(nf.nested, crumbs);
        }
        continue;
      }

      const newCrumbs = crumbs.concat([String(current ?? '(unknown)')]);
      const breadcrumb = newCrumbs.join(' > ');

      if (nf.frameStatus === 'removed') {
        lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(breadcrumb) }));
        lines.push(LangV6Config.frameCompareFrameRemoved());
        lines.push('');
        continue;
      } else if (nf.frameStatus === 'added') {
        lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(breadcrumb) }));
        lines.push(LangV6Config.frameCompareFrameAdded());
        lines.push('');
        continue;
      }

      // For changed frames/lights, show detailed changes
      if (nf.attributes?.length) {
        lines.push(LangV6Config.frameCompareBreadcrumbTitle({ breadcrumb: escapeMd(breadcrumb) }));
        renderAttributes(lines, nf.attributes);
      }
      if (nf.nested?.length) {
        renderNested(nf.nested, newCrumbs);
      }
    }
  }

  renderNested(report.nested || [], []);

  return lines.join('\n');
}

function renderAttributes(lines: string[], attributes: FrameAttributeDiff[]) {
  if (!attributes || attributes.length === 0) return;
  for (const a of attributes) {
    const rawLabel = a.description ?? a.code ?? '';
    const label = escapeMd(rawLabel);
    if (a.status === 'added') {
      const val = escapeMd(normalizeText(a.displayValue.after ?? a.value.after ?? ''));
      lines.push(LangV6Config.frameCompareAttributeAdded({ label, value: val }));
    } else if (a.status === 'removed') {
      const val = escapeMd(normalizeText(a.displayValue.before ?? a.value.before ?? ''));
      lines.push(LangV6Config.frameCompareAttributeRemoved({ label, value: val }));
    } else if (a.status === 'changed') {
      const beforeVal = escapeMd(normalizeText(a.displayValue.before ?? a.value.before ?? ''));
      const afterVal = escapeMd(normalizeText(a.displayValue.after ?? a.value.after ?? ''));
      lines.push(LangV6Config.frameCompareAttributeChanged({ label, before: beforeVal, after: afterVal }));
    }
  }
  lines.push('');
}

// --- Frame data normalizer for content-based deep comparison ---

/**
 * Converts a FrameV2 into a normalised shape for compareAnalyze:
 *  - Arrays keyed by identity field (reorder-proof)
 *  - Only user-visible design content retained
 *
 * Stripped (noise for comparison purposes):
 *  - boundary / bounds  – calculated geometry, not design content
 *  - validations         – compared separately by the handler
 *  - groupType           – internal categorisation enum
 *  - hideFromReport / hideFromUI / dataType – display metadata
 *  - attributeMeta       – runtime metadata, not design content
 *  - empty faces on nested frames – V1→V2 conversion artifact
 */
export function normalizeFrameForCompare(frame: FrameV2 | null | undefined): Record<string, any> | null {
  if (!frame) return null;

  const f = frame as Record<string, any>;
  const normalizedFaces = normalizeFaces(f.faces);
  return {
    description: f.description,
    productLine: f.productLine,
    attributeGroups: normalizeGroups(f.attributeGroups),
    ...(normalizedFaces ? { faces: normalizedFaces } : {}),
    ...(f.buyIn ? { buyIn: keyBy(f.buyIn, 'code', normalizeBuyIn) } : {})
  };
}

function normalizeGroups(
  groups: FrameAttributeGroup[] | null | undefined
): Record<string, any> {
  if (!groups) return {};
  return keyBy(groups, 'name', (g: FrameAttributeGroup) => ({
    name: g.name,
    description: g.description,
    attributes: keyBy(g.attributes, 'code', normalizeAttribute)
  }));
}

function normalizeAttribute(a: FrameAttribute): Record<string, any> {
  return {
    code: a.code,
    description: a.description,
    value: a.value,
    displayValue: a.displayValue
  };
}

function normalizeFaces(faces: FrameFace[] | null | undefined): Record<string, any> | null {
  if (!faces?.length) return null;
  // Filter out empty faces (V1→V2 artifact: faces with no lights or only empty lights)
  const meaningful = faces.filter(f => f.lights?.length > 0);
  if (meaningful.length === 0) return null;
  return keyBy(meaningful, 'faceId', face => ({
    faceId: face.faceId,
    name: face.name,
    lights: keyBy(face.lights, 'boundaryName', normalizeLight)
  }));
}

function normalizeLight(light: FrameLight): Record<string, any> {
  return {
    boundaryName: light.boundaryName,
    attributeGroups: normalizeGroups(light.attributeGroups),
    nestedFrames: keyBy(light.nestedFrames, 'description', nf => normalizeFrameForCompare(nf))
  };
}

function normalizeBuyIn(b: Record<string, any>): Record<string, any> {
  return {
    code: b.code,
    partCode: b.partCode,
    decription: b.decription,
    quantity: b.quantity,
    resourceType: b.resourceType,
    extraDetails: b.extraDetails
  };
}

function keyBy<T extends Record<string, any>>(
  arr: T[] | null | undefined,
  key: string,
  transform?: (item: T) => any
): Record<string, any> {
  if (!arr) return {};
  const result: Record<string, any> = {};
  for (const item of arr) {
    const k = String(item[key] ?? '');
    result[k] = transform ? transform(item) : item;
  }
  return result;
}
